import { Grid, Menu } from "@mui/material";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import isBetweenPlugin from "dayjs/plugin/isBetween";
import { styled } from "@mui/material/styles";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { PickersDay, PickersDayProps } from "@mui/x-date-pickers/PickersDay";

export interface DatePickerCalenderTypes {
  anchor: null | HTMLElement;
  calendarType: "Week" | "Month" | "Day" | "Range";
  startDate: Dayjs | null;
  endDate: Dayjs | null;
}

export interface DatePickerCalenderProps {
  props: DatePickerCalenderTypes;
  setProps: Dispatch<SetStateAction<DatePickerCalenderTypes>>;
  getDates: (props: DatePickerCalenderTypes) => void;
}

dayjs.extend(isBetweenPlugin);

interface CustomPickerDayProps extends PickersDayProps<Dayjs> {
  isSelected: boolean;
  isHovered: boolean;
}

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) => prop !== "isSelected" && prop !== "isHovered",
})<CustomPickerDayProps>(({ theme: muiTheme, isSelected, isHovered, day }) => ({
  borderRadius: 0,
  ...(isSelected && {
    backgroundColor: muiTheme.palette.primary.main,
    color: muiTheme.palette.primary.contrastText,
    "&:hover, &:focus": {
      backgroundColor: muiTheme.palette.primary.main,
    },
  }),
  ...(isHovered && {
    backgroundColor: muiTheme.palette.primary[muiTheme.palette.mode],
    "&:hover, &:focus": {
      backgroundColor: muiTheme.palette.primary[muiTheme.palette.mode],
    },
  }),
  ...(day.day() === 0 && {
    borderTopLeftRadius: "50%",
    borderBottomLeftRadius: "50%",
  }),
  ...(day.day() === 6 && {
    borderTopRightRadius: "50%",
    borderBottomRightRadius: "50%",
  }),
})) as React.ComponentType<CustomPickerDayProps>;

const isInSameWeek = (dayA: Dayjs, dayB: Dayjs | null | undefined) => {
  if (dayB == null) {
    return false;
  }

  return dayA.isSame(dayB, "week");
};

function Day(
  props: PickersDayProps<Dayjs> & {
    selectedDay?: Dayjs | null;
    hoveredDay?: Dayjs | null;
  }
) {
  const { day, selectedDay, hoveredDay, ...other } = props;

  return (
    <CustomPickersDay
      {...other}
      day={day}
      sx={{ px: 2.5 }}
      disableMargin
      selected={false}
      isSelected={isInSameWeek(day, selectedDay)}
      isHovered={isInSameWeek(day, hoveredDay)}
    />
  );
}

const DatePickerCalender = ({
  props,
  setProps,
  getDates,
}: DatePickerCalenderProps) => {
  const [hoveredDay, setHoveredDay] = React.useState<Dayjs | null>(null);

  const handleClose = () => {
    setProps((prev) => ({
      ...prev,
      anchor: null,
    }));
  };

  const getWeekStartEndDate = (start: boolean, date: Dayjs) => {
    let tempDate = start ? date.subtract(1, "day") : date.add(1, "day");
    while (tempDate.isSame(date, "week")) {
      tempDate = start ? tempDate.subtract(1, "day") : tempDate.add(1, "day");
    }
    return start ? tempDate.add(1, "day") : tempDate.subtract(1, "day");
  };

  const setDates = (date: Dayjs | null, start: boolean) => {
    if (!start || props.calendarType !== "Range") {
      setProps((prev) => ({
        ...prev,
        anchor: null,
      }));
    }

    if (start) {
      setProps((prev) => ({
        ...prev,
        startDate: date,
      }));
    } else {
      setProps((prev) => ({
        ...prev,
        endDate: date,
      }));
    }

    if (date) {
      switch (props.calendarType) {
        case "Day":
        case "Month":
          getDates({ ...props, startDate: date, endDate: date });
          break;
        case "Week":
          getDates({
            ...props,
            startDate: getWeekStartEndDate(true, date),
            endDate: getWeekStartEndDate(false, date),
          });
          setProps((prev) => ({
            ...prev,
            startDate: getWeekStartEndDate(true, date),
            endDate: getWeekStartEndDate(false, date),
          }));
          break;
        case "Range":
          if (start) {
            getDates({
              ...props,
              startDate: date,
            });
          } else {
            getDates({
              ...props,
              endDate: date,
            });
          }
          break;
        default:
          return;
      }
    }
  };

  return (
    <Grid>
      <Menu
        anchorEl={props.anchor}
        open={props.anchor ? true : false}
        onClose={handleClose}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid display={"flex"}>
            <DateCalendar
              value={props.startDate}
              onChange={(newValue) => setDates(newValue, true)}
              showDaysOutsideCurrentMonth
              slots={props.calendarType === "Week" ? { day: Day } : undefined}
              slotProps={
                props.calendarType === "Week"
                  ? {
                      day: (ownerState) =>
                        ({
                          selectedDay: props.startDate,
                          hoveredDay,
                          onPointerEnter: () => setHoveredDay(ownerState.day),
                          onPointerLeave: () => setHoveredDay(null),
                        } as any),
                    }
                  : undefined
              }
              view={
                props.calendarType === "Month"
                  ? "month"
                  : props.calendarType === "Day"
                  ? "day"
                  : undefined
              }
              minDate={
                props.calendarType === "Range"
                  ? dayjs().subtract(6, "month")
                  : undefined
              }
              disableFuture
            />
            {props.calendarType === "Range" && (
              <DateCalendar
                value={props.endDate}
                onChange={(newValue) => setDates(newValue, false)}
                showDaysOutsideCurrentMonth
                minDate={
                  props.calendarType === "Range"
                    ? dayjs().subtract(6, "month")
                    : undefined
                }
                maxDate={
                  props.calendarType === "Range"
                    ? props.startDate?.add(1, "month")
                    : undefined
                }
                disableFuture
              />
            )}
          </Grid>
        </LocalizationProvider>
      </Menu>
    </Grid>
  );
};

export default DatePickerCalender;
